/**
 * Anagram reducer (continuous-play + diminishing-payout variant).
 *
 * Pure function: takes a state + action, returns the next state (or the same
 * reference when nothing changed, so subscribers can skip).
 *
 * Scene routing rules:
 *   - LOBBY (no round, no reveal, no podium)  -> lobby
 *   - an_round_open                            -> round
 *   - an_round_end                             -> reveal
 *   - an_game_over                             -> podium
 *   - WS connection lost                       -> connection_lost
 */

import type { OutMsg } from '@sg/shared';
import type { BrowserState, SceneName } from './types.js';

export type Action =
  | { type: 'ws'; msg: OutMsg }
  | { type: 'connection'; value: BrowserState['connection'] };

function buildInitialState(): BrowserState {
  const s: Partial<BrowserState> = {};
  s.scene = 'lobby';
  s.connection = 'idle';
  s.tenantId = '';
  s.state = 'LOBBY';
  s.theme = null;
  s.leaderboard = [];
  s.leaderboardAllTime = [];
  s.sessionLeaderboard = [];
  s.round = null;
  s.reveal = null;
  s.podium = null;
  return s as BrowserState;
}

export function initialState(): BrowserState {
  return buildInitialState();
}

export function reduce(state: BrowserState, action: Action): BrowserState {
  if (action.type === 'connection') {
    if (state.connection === action.value) return state;
    const scene: SceneName = action.value === 'open' ? state.scene : 'connection_lost';
    return { ...state, connection: action.value, scene };
  }
  return reduceWs(state, action.msg);
}

function reduceWs(state: BrowserState, msg: OutMsg): BrowserState {
  switch (msg.type) {
    case 'state_snapshot': {
      // The /browser channel fans every tenant FSM's snapshot to every
      // browser source — even if the URL pinned ?game=anagram, snapshots
      // from a parallel WhatIs/SurveySays FSM still arrive. Pull theme +
      // leaderboards (those are tenant-wide) but DO NOT trust scene
      // transitions from a snapshot when an Anagram round is in flight:
      // we only respect lobby/podium when the local round is null. This
      // prevents another game's PODIUM snapshot from yanking the screen
      // out of an active jumble.
      const s = msg.state;
      const isLobby = s.state.startsWith('LOBBY');
      const isPodium = s.state.startsWith('PODIUM');
      const inAnagramRound = state.round !== null || state.reveal !== null;
      const allowSceneSwitch = !inAnagramRound;
      const nextScene = allowSceneSwitch
        ? (isLobby ? 'lobby' : isPodium ? 'podium' : state.scene)
        : state.scene;
      return {
        ...state,
        tenantId: s.tenant_id,
        state: s.state,
        theme: s.theme ?? state.theme,
        leaderboard: s.leaderboard_top10 ?? state.leaderboard,
        leaderboardAllTime: s.leaderboard_all_time ?? state.leaderboardAllTime,
        round: isLobby && allowSceneSwitch ? null : state.round,
        reveal: isLobby && allowSceneSwitch ? null : state.reveal,
        podium: isLobby && allowSceneSwitch ? null : state.podium,
        scene: nextScene,
      };
    }
    case 'an_round_open': {
      return {
        ...state,
        scene: 'round',
        reveal: null,
        round: {
          wordId: msg.word_id,
          jumbled: msg.jumbled,
          answer: msg.answer,
          category: msg.category,
          difficulty: msg.difficulty,
          index: msg.round_index,
          total: msg.rounds_total,
          startsAtMs: msg.starts_at_ms,
          endsAtMs: msg.ends_at_ms,
          countdownStartsAtMs: msg.countdown_starts_at_ms ?? null,
          hintAtMs: msg.hint_at_ms,
          hintLetter: null,
          hintReveals: [],
          corrects: [],
        },
      };
    }
    case 'an_hint_reveal': {
      const r = state.round;
      if (!r || r.wordId !== msg.word_id) return state;
      // Append (idempotent on position) so progressive reveals accumulate.
      // Backend always sends a fresh position each frame, but if a network
      // resnap re-sends an already-revealed index we silently de-dup.
      if (r.hintReveals.some((h) => h.position === msg.hint_index)) return state;
      return {
        ...state,
        round: {
          ...r,
          hintLetter: msg.hint_letter,
          hintReveals: [
            ...r.hintReveals,
            { position: msg.hint_index, letter: msg.hint_letter },
          ],
        },
      };
    }
    case 'an_correct_answer': {
      const r = state.round;
      if (!r || r.wordId !== msg.word_id) return state;
      // Append unless we already recorded this rank (idempotent on resnaps).
      if (r.corrects.some((c) => c.rank === msg.rank)) return state;
      return {
        ...state,
        round: {
          ...r,
          corrects: [
            ...r.corrects,
            {
              rank: msg.rank,
              player_id: msg.player_id,
              display_name: msg.display_name,
              points: msg.points,
              elapsed_ms: msg.elapsed_ms,
            },
          ],
        },
      };
    }
    case 'an_round_end': {
      const corrects = msg.correct_answers.map((c) => ({
        rank: c.rank,
        player_id: c.player_id,
        display_name: c.display_name,
        points: c.points,
        elapsed_ms: c.elapsed_ms,
      }));
      const headline = corrects[0] ?? null;
      return {
        ...state,
        scene: 'reveal',
        reveal: {
          wordId: msg.word_id,
          answer: msg.answer,
          category: msg.category,
          index: msg.round_index,
          total: msg.rounds_total,
          firstCorrect: headline
            ? {
                displayName: headline.display_name,
                points: headline.points,
                elapsedMs: headline.elapsed_ms,
              }
            : null,
          corrects,
        },
      };
    }
    case 'an_session_leaderboard': {
      return {
        ...state,
        sessionLeaderboard: msg.rows.map((r) => ({
          rank: r.rank,
          player_id: r.player_id,
          display_name: r.display_name,
          total_points: r.total_points,
        })),
      };
    }
    case 'an_game_over': {
      return { ...state, scene: 'podium', podium: msg.podium };
    }
    default:
      return state;
  }
}
